import { useEffect, useMemo, useState } from 'react'
import { NavigateFunction, useLocation } from 'react-router-dom'

import { giftBonus } from '@/assets/icons'
import Container from '@/components/atoms/container/container'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import { VoucherPurchaseStep } from '@/data/enums/voucherPurchaseSteps'
import { useCheckInternetConnection } from '@/hooks'
import { useTranslate } from '@/i18n/useTranslate'
import { useOrder } from '@/providers'
import { cmsBettingVendorService } from '@/services/cmsBettingVendorService'
import { depositBonusService } from '@/services/depositBonusService'
import { getVendorBonusEnabled } from '@/services/deviceConfigurationService'
import type { CmsBettingVendor } from '@/types/cms/bettingVendor'
import { VoucherResponse } from '@/types/pos/deposit'
import type { DepositBonusPromotion } from '@/types/pos/depositBonus'
import {
    formatPromotionTotalAmount,
    getPromotionTotalAmount,
    mapPromotionsByWalletId
} from '@/utilities/depositBonusHelper'

type BettingServiceSelectionTemplateProps = {
    navigate: NavigateFunction
}

type ServiceItem = {
    id: string
    name: string
    logo: string
    vendor: CmsBettingVendor
    isComingSoon?: boolean
}

export default function BettingServiceSelectionTemplate({
    navigate
}: BettingServiceSelectionTemplateProps) {
    const { t } = useTranslate()
    const location = useLocation()
    const { deviceConfiguration } = useCheckInternetConnection()
    const vendorBonusEnabled = getVendorBonusEnabled(deviceConfiguration)
    const { setSelectedBettingVendor, setCurrentStep, state } = useOrder()
    const locationState = location.state as
        | {
              fromDirectBettingConfirmation?: boolean
              selectedBettingService?: string
              accountId?: string
              createdVoucherData?: VoucherResponse
          }
        | undefined
    const [vendors, setVendors] = useState<CmsBettingVendor[]>([])
    const [promotions, setPromotions] = useState<DepositBonusPromotion[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const paidAmount = state.amount ?? null
    const promotionsByWalletId = useMemo(
        () => mapPromotionsByWalletId(promotions),
        [promotions]
    )

    useEffect(() => {
        if (!state.directBettingCompleted) {
            return
        }

        setCurrentStep(VoucherPurchaseStep.PROCESS_PAYMENT)
        navigate('/buy-voucher-cash', {
            replace: true,
            state: {
                voucherType: 'betting',
                bettingFlow: 'direct',
                selectedBettingService:
                    locationState?.selectedBettingService ??
                    state.selectedBettingVendor?.slug,
                accountId:
                    locationState?.accountId ??
                    state.directBettingAccountId ??
                    undefined,
                createdVoucherData:
                    state.createdVoucherData ?? locationState?.createdVoucherData
            }
        })
    }, [
        locationState?.accountId,
        locationState?.createdVoucherData,
        locationState?.selectedBettingService,
        navigate,
        setCurrentStep,
        state.createdVoucherData,
        state.directBettingAccountId,
        state.directBettingCompleted,
        state.selectedBettingVendor?.slug
    ])

    useEffect(() => {
        let isMounted = true
        setIsLoading(true)
        setError(null)

        const vendorsPromise = cmsBettingVendorService.getBettingVendors()
        const promotionsPromise = vendorBonusEnabled
            ? depositBonusService
                  .getActivePromotions()
                  .catch(() => ({ promotions: [] as DepositBonusPromotion[] }))
            : Promise.resolve({ promotions: [] as DepositBonusPromotion[] })

        Promise.all([vendorsPromise, promotionsPromise])
            .then(([vendorResult, promotionsResult]) => {
                if (!isMounted) return
                setVendors(vendorResult)
                setPromotions(promotionsResult.promotions ?? [])
            })
            .catch((err: unknown) => {
                if (!isMounted) return
                setError(
                    err instanceof Error
                        ? err.message
                        : t('bettingServiceSelection.error')
                )
            })
            .finally(() => {
                if (!isMounted) return
                setIsLoading(false)
            })

        return () => {
            isMounted = false
        }
    }, [t, vendorBonusEnabled])

    const derivedServices = useMemo(() => {
        const available = vendors.filter(
            (vendor) => vendor.status === 'available'
        )

        const popular = available.slice(0, 2).map((vendor) => ({
            id: vendor.slug,
            name: vendor.name.toUpperCase(),
            logo:
                vendor.sutImageUrl ||
                vendor.wideImageUrl ||
                vendor.squareImageUrl ||
                '',
            vendor
        }))

        const others = available.slice(2).map((vendor) => ({
            id: vendor.slug,
            name: vendor.name.toUpperCase(),
            logo:
                vendor.sutImageUrl ||
                vendor.wideImageUrl ||
                vendor.squareImageUrl ||
                '',
            vendor
        }))

        const comingSoon = vendors
            .filter((vendor) => vendor.status === 'coming-soon')
            .map((vendor) => ({
                id: vendor.slug,
                name: vendor.name.toUpperCase(),
                logo:
                    vendor.sutImageUrl ||
                    vendor.wideImageUrl ||
                    vendor.squareImageUrl ||
                    '',
                vendor,
                isComingSoon: true
            }))

        return {
            popular,
            others,
            comingSoon
        }
    }, [vendors])

    const handleServiceSelect = (service: ServiceItem) => {
        if (service.isComingSoon || state.directBettingCompleted) {
            return
        }

        // Prefer the persisted context so the voucher / accountId survive even
        // when we are entered here without navigation state (e.g. via the
        // header back button from the vendor-account screen).
        const persistedVoucher =
            state.createdVoucherData ?? locationState?.createdVoucherData
        const persistedAccountId =
            locationState?.accountId ?? state.directBettingAccountId ?? undefined
        const isReturnToDirectConfirmation =
            Boolean(persistedVoucher) ||
            locationState?.fromDirectBettingConfirmation === true

        setSelectedBettingVendor(service.vendor)
        navigate('/betting-vendor-account', {
            state: {
                voucherType: 'betting',
                bettingFlow: 'direct',
                selectedBettingService: service.id,
                fromDirectBettingConfirmation: isReturnToDirectConfirmation,
                accountId: persistedAccountId,
                createdVoucherData: persistedVoucher
            }
        })
    }

    const getVendorPromotion = (vendor: CmsBettingVendor) => {
        if (!vendorBonusEnabled || vendor.walletId == null) {
            return undefined
        }
        return promotionsByWalletId.get(vendor.walletId)
    }

    const renderBonusBadge = (promotion: DepositBonusPromotion) => {
        const totalAmount = getPromotionTotalAmount(promotion)
        if (totalAmount <= 0) {
            return null
        }

        return (
            <span className="betting-service-selection__bonus-badge">
                <img src={giftBonus} alt="" />
                {t('bettingServiceSelection.bonusBadge', {
                    amount: formatPromotionTotalAmount(totalAmount),
                    currency: promotion.currencyCode
                })}
            </span>
        )
    }

    const renderVendorCard = (
        service: ServiceItem,
        variant: 'popular' | 'compact'
    ) => {
        const promotion = getVendorPromotion(service.vendor)
        const hasBonus = Boolean(
            promotion && getPromotionTotalAmount(promotion) > 0
        )
        const cardClassName = [
            'betting-service-selection__card',
            `betting-service-selection__card--${variant}`,
            hasBonus ? 'betting-service-selection__card--bonus' : ''
        ]
            .filter(Boolean)
            .join(' ')
        const logoClassName =
            variant === 'compact'
                ? 'betting-service-selection__logo betting-service-selection__logo--compact'
                : 'betting-service-selection__logo'

        return (
            <button
                key={service.id}
                className={cardClassName}
                onClick={() => handleServiceSelect(service)}>
                <div className="betting-service-selection__card-visual">
                    {hasBonus && promotion ? renderBonusBadge(promotion) : null}
                    <div className={logoClassName}>
                        {service.logo ? (
                            <img src={service.logo} alt="" />
                        ) : (
                            <span className="betting-service-selection__logo-fallback">
                                {service.name}
                            </span>
                        )}
                    </div>
                </div>
                <p>{service.name}</p>
            </button>
        )
    }

    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl="/betting-flow-selection"
                navigationBackText={t('header.back')}
            />
            <div className="betting-service-selection">
                {isLoading ? (
                    <div className="betting-service-selection__loading-screen">
                        <div className="betting-service-selection__spinner" />
                        <p className="betting-service-selection__loading">
                            {t('bettingServiceSelection.loading')}
                        </p>
                    </div>
                ) : error ? (
                    <div className="betting-service-selection__message-screen">
                        <p className="betting-service-selection__message">
                            {t('bettingServiceSelection.error')}
                        </p>
                    </div>
                ) : vendors.length === 0 ? (
                    <div className="betting-service-selection__message-screen">
                        <p className="betting-service-selection__message">
                            {t('bettingServiceSelection.empty')}
                        </p>
                    </div>
                ) : (
                    <>
                        {paidAmount !== null && (
                            <div className="betting-service-selection__amount">
                                {t('bettingServiceSelection.amountLabel', {
                                    amount: paidAmount
                                })}
                            </div>
                        )}

                        <h1>{t('bettingServiceSelection.title')}</h1>

                        {derivedServices.popular.length > 0 && (
                            <section>
                                <h2>{t('bettingServiceSelection.popular')}</h2>
                                <div className="betting-service-selection__cards betting-service-selection__cards--popular">
                                    {derivedServices.popular.map((service) =>
                                        renderVendorCard(service, 'popular')
                                    )}
                                </div>
                            </section>
                        )}

                        {derivedServices.others.length > 0 && (
                            <section>
                                <h2>{t('bettingServiceSelection.others')}</h2>
                                <div className="betting-service-selection__cards betting-service-selection__cards--compact">
                                    {derivedServices.others.map((service) =>
                                        renderVendorCard(service, 'compact')
                                    )}
                                </div>
                            </section>
                        )}

                        {derivedServices.comingSoon.length > 0 && (
                            <section>
                                <h2>{t('bettingServiceSelection.comingSoon')}</h2>
                                <div className="betting-service-selection__cards betting-service-selection__cards--compact">
                                    {derivedServices.comingSoon.map(
                                        (service) => (
                                            <div
                                                key={service.id}
                                                className="betting-service-selection__card betting-service-selection__card--compact betting-service-selection__card--soon">
                                                <span className="betting-service-selection__soon-badge">
                                                    {t(
                                                        'bettingServiceSelection.soonBadge'
                                                    )}
                                                </span>
                                                <div className="betting-service-selection__logo betting-service-selection__logo--compact">
                                                    {service.logo ? (
                                                        <img
                                                            src={service.logo}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <span className="betting-service-selection__logo-fallback">
                                                            {service.name}
                                                        </span>
                                                    )}
                                                </div>
                                                <p>{service.name}</p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
            <Footer />
        </Container>
    )
}
